import React, { useCallback, useEffect, useState } from 'react';

import {
  Badge,
  Button,
  Modal,
  Pagination,
  Popover,
  TableData,
} from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { SearchSelect } from '@/design-system/search-select';
import { Address, addressesAPI } from '@/services/api/addresses';
import { useProvidersDropdown } from '@/pages/shipments/components/useProvidersDropdown';
import { Column } from '@/types/common';

import AddressFormModal from './components/AddressFormModal';
import ImportModal from './components/ImportModal';

const AddressesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [items, setItems] = useState<Address[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // ── Client filter (applied + popover draft) ────────────────────────────────
  const [clientIds, setClientIds] = useState<number[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [clientIdsDraft, setClientIdsDraft] = useState<number[]>([]);
  // Server-side searchable + paginated client list — fetched while the filter
  // popover is open (mirrors the CreateDispatch modal's client picker).
  const providers = useProvidersDropdown(filterOpen);

  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState<Address | null>(null);
  const [deleting, setDeleting] = useState<Address | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await addressesAPI.list({
        search: debouncedSearch || undefined,
        page,
        limit: pageSize,
        clientIds: clientIds.length > 0 ? clientIds : undefined,
      });
      setItems(res.addresses);
      setTotal(res.total);
    } catch (err) {
      setItems([]);
      setTotal(0);
      showErrorToast(
        'Gagal memuat address',
        err instanceof Error ? err.message : 'Unknown error'
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, pageSize, clientIds]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Seed the popover draft from the applied filter each time it opens.
  const openFilter = () => {
    setClientIdsDraft(clientIds);
    setFilterOpen(true);
  };
  const applyFilter = () => {
    setClientIds(clientIdsDraft);
    setPage(1);
    setFilterOpen(false);
  };
  const resetFilter = () => {
    setClientIdsDraft([]);
    setClientIds([]);
    setPage(1);
    setFilterOpen(false);
  };

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (a: Address) => {
    setEditing(a);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await addressesAPI.remove(deleting.id);
      showSuccessToast(
        'Address dihapus',
        `${deleting.originName} → ${deleting.destinationName}`
      );
      setDeleting(null);
      // If we just emptied the last row on a page, step back a page.
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchList();
    } catch (err) {
      showErrorToast(
        'Gagal menghapus address',
        err instanceof Error ? err.message : 'Unknown error'
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const nameCls = 'text-[13px] text-gray-900 tracking-tight';
  const metaCls = 'text-[11px] text-gray-500 truncate max-w-[260px]';

  const columns: Column<Address>[] = [
    {
      key: 'client',
      title: 'Client',
      render: (_v, r) => (
        <span className={nameCls}>
          {r.client?.name || <span className="text-gray-300">—</span>}
        </span>
      ),
    },
    {
      key: 'origin',
      title: 'Origin',
      render: (_v, r) => (
        <div>
          <p className={nameCls}>{r.originName}</p>
          <p className={metaCls}>{r.originAddress}</p>
        </div>
      ),
    },
    {
      key: 'destination',
      title: 'Destination',
      render: (_v, r) => (
        <div>
          <p className={nameCls}>{r.destinationName}</p>
          <p className={metaCls}>{r.destinationAddress}</p>
        </div>
      ),
    },
    {
      key: 'state',
      title: 'State',
      render: (_v, r) =>
        r.state === 'DRAFT' ? (
          <Badge tone="amber" dot>
            Draft
          </Badge>
        ) : (
          <Badge tone="success" dot>
            Confirmed
          </Badge>
        ),
    },
    {
      key: 'distance',
      title: 'Distance',
      align: 'right',
      render: (_v, r) => (
        <span className="font-mono text-[12px] text-gray-700 tabular-nums">
          {r.distance.toLocaleString()} m
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (_v, r) => (
        <div className="inline-flex items-center justify-end gap-1">
          <Button
            variant="outline-accent"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              openEdit(r);
            }}
          >
            Edit
          </Button>
          {/* Delete disembunyikan sementara — aktifkan lagi kalau sudah boleh hapus lane.
          <Button
            variant="outline-danger"
            size="xs"
            onClick={(e) => {
              e?.stopPropagation();
              setDeleting(r);
            }}
          >
            Delete
          </Button>
          */}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-col gap-3 border-b border-gray-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">
            <span className="text-primary font-bold mr-3">03</span>
            Master data · Address
          </p>
          <h1 className="text-[28px] font-extrabold tracking-tight text-gray-900 leading-none">
            Addresses
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 leading-relaxed">
            Master lane origin → destination + jarak. Dipakai ulang sebagai
            cache geocode saat import shipment.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="white" onClick={fetchList}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
          <Button variant="white" onClick={() => setImportOpen(true)}>
            <i className="ri-upload-2-line mr-1.5 text-[14px]" />
            Import
          </Button>
          <Button onClick={openCreate}>
            <i className="ri-add-line mr-1.5 text-[16px]" />
            Address baru
          </Button>
        </div>
      </header>

      <div className="rounded-gsm border border-gray-200 bg-white">
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-4 py-3">
          <div className="relative flex max-w-xs flex-1 items-center">
            <i className="ri-search-line pointer-events-none absolute left-3 text-[14px] text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari nama / alamat…"
              className="w-full rounded-gsm border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="relative">
            <Button variant="white" onClick={openFilter}>
              <i className="ri-equalizer-2-line mr-1.5 text-[14px]" />
              Filter
              {clientIds.length > 0 && (
                <span className="ml-1.5 rounded-gsm-sm bg-primary px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white">
                  {clientIds.length}
                </span>
              )}
            </Button>
            <Popover
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              align="end"
              width={340}
              title="Filter addresses"
              headerExtra={
                <button
                  type="button"
                  onClick={resetFilter}
                  className="text-[11px] font-medium text-gray-500 transition-colors hover:text-accent"
                >
                  Reset
                </button>
              }
              footer={
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant="white"
                    size="small"
                    onClick={() => setFilterOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button size="small" onClick={applyFilter}>
                    Apply
                  </Button>
                </div>
              }
            >
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">
                  Client
                </label>
                <SearchSelect
                  multiple
                  placeholder="All clients"
                  options={[
                    ...providers.options.map((p) => ({
                      value: p.id,
                      label: p.name,
                    })),
                    // Keep already-selected clients labelled even when the
                    // current server page doesn't include them (chip would
                    // otherwise fall back to the raw id).
                    ...clientIdsDraft
                      .filter(
                        (id) => !providers.options.some((p) => p.id === id)
                      )
                      .map((id) => ({
                        value: id,
                        label: providers.labelFor(id) || String(id),
                      })),
                  ]}
                  value={clientIdsDraft}
                  onChange={(next) =>
                    setClientIdsDraft(next.map((v) => Number(v)))
                  }
                  onSearchChange={providers.setSearch}
                  disableLocalFilter
                  loading={providers.loading}
                  hasMore={providers.hasMore}
                  onLoadMore={providers.loadMore}
                  emptyMessage={providers.error ?? 'No matching client'}
                />
                {providers.error && (
                  <p className="mt-1 text-[11px] text-error-base">
                    {providers.error}
                  </p>
                )}
              </div>
            </Popover>
          </div>
        </div>

        <div className="p-4">
          <TableData<Address>
            data={items}
            columns={columns}
            loading={loading}
            size="small"
          />
          {total > 0 && (
            <div className="mt-3">
              <Pagination
                currentPage={page}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </div>
          )}
        </div>
      </div>

      <AddressFormModal
        isOpen={formOpen}
        address={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={() => fetchList()}
      />

      <ImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => (page === 1 ? fetchList() : setPage(1))}
      />

      <Modal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        icon="ri-delete-bin-line"
        iconTone="danger"
        title="Hapus address lane?"
        subtitle={
          deleting
            ? `${deleting.originName} → ${deleting.destinationName}`
            : undefined
        }
        width="sm"
        closeOnBackdropClick={!deleteBusy}
        closeOnEscape={!deleteBusy}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="white"
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Batal
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteBusy}>
              {deleteBusy ? 'Menghapus…' : 'Hapus'}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          Lane ini akan dihapus permanen. Cache geocode-nya juga hilang — import
          berikutnya untuk pasangan nama ini akan di-resolve ulang.
        </p>
      </Modal>
    </div>
  );
};

export default AddressesPage;
